import {
  Design,
  DesignField,
  DesignScreenGroup,
  DesignSourceType,
  DesignThumbnailElement,
  NewDesign
} from 'services/entities/designs';
import {callAPI, API_METHOD} from 'services/api';
import {NotFoundError, ValueError} from 'util/errors';
import config from 'config';
import {readSpace, readSpaceScreenGroup} from 'services/spaces';
import {Bounds2D} from 'util/geometry';
import {DateTime} from 'util/time';
import {Validators} from "util/validation";

async function callAuthenticatedAPI(url: string, method: API_METHOD, body: any = null): Promise<any> {
  return await callAPI(url, method, body, true);
}

export async function createDesign(design: NewDesign):Promise<Design> {
  const requestBody = { name: design.name, space: design.space, source_type: design.sourceType.toString() };
  let response;
  try {
    response = await callAuthenticatedAPI('/designs/', 'POST', requestBody);
  } catch(err) {
    if(err instanceof ValueError && 'source_type' in err.fieldErrors) {
      err.fieldErrors['sourceType'] = err.fieldErrors['source_type'];
    }
    throw err;
  }
  const { source_type, ...properties } = response;
  return { sourceType: source_type, ...properties };
}

function readDesignThumbnailElement(data: any): DesignThumbnailElement {
  const { x, y, width, height, scale_x, scale_y, rotation, depth, thumbnails } = data;
  return {
    bounds: { x: x, y: y, width: width * scale_x, height: height * scale_y },
    rotation: rotation,
    depth: depth,
    thumbnails: thumbnails.map((t:any) => t.file)
  };
}

export function readDesignScreenGroup(data: any, sourceType: DesignSourceType): DesignScreenGroup {
  const { screen_group, source_data, sync_status, thumbnail_image, element_sources, thumbnail_elements, ...props } = data;
  return {
    screenGroup: typeof(screen_group) === 'object' ? readSpaceScreenGroup(screen_group) : undefined,
    sourceData: source_data,
    syncStatus: sync_status,
    thumbnailImageURL: thumbnail_image ? new URL(thumbnail_image, config.api.url) : null,
    elementSources: element_sources || null,
    thumbnailElements: thumbnail_elements ? thumbnail_elements.map(readDesignThumbnailElement) : null,
    ...props
  } as DesignScreenGroup;
}

export function readDesign(data: any): Design {
  const { source_type, space, screen_groups, created, ...props } = data;
  return {
    sourceType: source_type,
    space: readSpace(space),
    created: DateTime.parse(created),
    screenGroups: screen_groups ? screen_groups.map((sg:any) => readDesignScreenGroup(sg, source_type)) : null,
    ...props
  } as Design;
}

export async function loadDesigns(filters?:{space?: string}, fields?: DesignField[]): Promise<Design[]> {
  const fieldsStr = fields ? fields.join(',') : '';
  const space = filters ? filters.space || '' : '';
  const designs = await callAPI(`/designs/?space=${space}&fields=${fieldsStr}`, 'GET', null, true);
  return designs.map(readDesign);
}

// TODO: caching?
export async function loadDesign(code: string, ...fields: DesignField[]): Promise<Design> {
  const fieldsStr = fields ? fields.join(',') : '';
  const design = await callAPI(`/designs/${code}/?fields=${fieldsStr}`, 'GET', null, true);
  if(!design) {
    throw new NotFoundError(`Design not found: ${code}`);
  }
  return readDesign(design);
}

export async function loadDesignVideo(code: string, screenGroupCode: string): Promise<{videoURL: string, bounds: Bounds2D}> {
  const response = await callAPI(`/designs/${code}/video/${screenGroupCode}/`, 'GET', null, true);
  return {
    videoURL: response.video_url,
    bounds: response.bounds
  };
}

export function validateDesignVideoURL(url: string): boolean {
  if(!url) {
    return true;
  } else if(!Validators.url(url)) {
    return true;
  }
  if(!/^[a-zA-Z]+:\/\//.test(url)) {
    url = 'http://' + url;
  }
  const parsedURL = new URL(url);
  if(parsedURL.hostname === 'www.youtube.com' || parsedURL.hostname.endsWith('.youtube.com')) {
    return parsedURL.pathname === '/watch';
  } else {
    return parsedURL.hostname === 'youtu.be' || parsedURL.hostname.endsWith('.youtu.be');
  }
}

function extractDesignVideoID(videoURL: string): string|null {
  if(!videoURL) {
    return null;
  }
  let url;
  try {
    url = new URL(videoURL);
  } catch(ex) {
    return null;
  }
  if(url.pathname === '/watch') {
    return url.searchParams.get('v');
  } else {
    return url.pathname.slice(1);
  }
}

export async function validateDesignVideoExists(videoURL: string): Promise<boolean> {
  if(!videoURL) {
    return true;
  }
  const videoID = extractDesignVideoID(videoURL);
  if(!videoID) {
    return false;
  }
  const headers: any = { 'Content-Type': 'application/json' };
  const url = `https://youtube.googleapis.com/youtube/v3/videos?part=contentDetails&id=${videoID}&key=${config.youTube.apiKey}`;
  const response = await fetch(url, { headers: headers, method: 'GET' });
  switch(response.status) {
    case 200:
      const responseData: any = await response.json();
      return responseData['items'].length > 0;
    default:
      console.warn(`Request failed: ${response.status} - ${response.statusText}`);
      // unable to validate video - assume it's public
      return true;
  }
}

export async function setDesignVideo(code: string, screenGroupCode: string, videoURL: string,
                                        bounds: Bounds2D): Promise<any> {
  await callAPI(`/designs/${code}/video/${screenGroupCode}/`, 'PUT', {
    video_url: videoURL,
    bounds: bounds
  }, true);
}

export async function removeDesignVideo(code: string, screenGroupCode: string): Promise<void> {
  await callAPI(`/designs/${code}/video/${screenGroupCode}/`, 'DELETE', null, true);
}

export async function deleteDesign(code: string) {
  await callAuthenticatedAPI(`/designs/${code}/`, 'DELETE');
}